/**
 * studioLayers — the Test-Fit Studio's unified layer view-model.
 *
 * The studio renders many kinds of canvas element (parcels, site-reference
 * layers, generated equipment, acoustic barriers, exclusions, analysis
 * overlays). This module derives, from the generation result + site data, the
 * *linked* facts each layer should surface — so the layer tree, the type-aware
 * Inspector and the canvas all read from ONE description of "what this layer is
 * and where its data came from" instead of re-deriving it per surface.
 *
 * It is a pure, read-only projection of `testFit`/`result`/`site` — it never
 * mutates the frozen layout input. (View-state — visibility/lock/opacity/order
 * — lives separately in `studioLayerMemory`.)
 *
 * Built incrementally: the first consumer is the generated-component row's
 * dimension-provenance chip (so an OEM-installed footprint is distinguishable
 * from a planning-acreage surrogate at the layer level, not only in the
 * Inspector). Further builders land as the shared LayerPalette + type-aware
 * Inspector consume them.
 */
import { CATEGORY_LABELS, CATEGORY_ORDER, SOURCE_BADGES, colorForCategory } from '../../../../config/siteLayerColors';
import type { GenerationTestFitDimensionBasis } from '../../../../lib/generationImpact/generationTestFit';
import type {
  GenerationAcousticBarrier,
  GenerationLayoutExclusionZone,
} from '../../../../types/generationImpact';
import type { SiteLayer } from '../../../../types/site';

/**
 * Where a generated component's footprint dimensions came from — the honest
 * provenance behind its geometry. `oem` is a real OEM installed envelope;
 * `surrogate` is the planning-acreage land basis (0.4 acre/MW), which is NOT an
 * installed footprint and varies with the planning derate.
 */
export type DimensionProvenanceTone = 'oem' | 'surrogate' | 'reserved' | 'user';

export interface DimensionProvenance {
  /** Short chip label for a layer row (≤ ~8 chars). */
  badge: string;
  /** Full description for the chip's title / tooltip. */
  title: string;
  tone: DimensionProvenanceTone;
  /** True only for real OEM installed dimensions (the trustworthy footprint). */
  isInstalled: boolean;
}

const DIMENSION_PROVENANCE: Record<GenerationTestFitDimensionBasis, DimensionProvenance> = {
  'project-installed-dimensions': {
    badge: 'OEM',
    title: 'OEM installed dimensions — a project-specific installed footprint.',
    tone: 'oem',
    isInstalled: true,
  },
  'planning-acreage': {
    badge: 'Planning',
    title:
      'Planning-acreage surrogate (0.4 acre/MW land basis). NOT an OEM installed footprint; '
      + 'it varies with the planning derate. Confirm against vendor dimensions before relying on the fit.',
    tone: 'surrogate',
    isInstalled: false,
  },
  'project-route-reservation': {
    badge: 'Route',
    title: 'Project route reservation — a reserved corridor width, not an installed footprint.',
    tone: 'reserved',
    isInstalled: false,
  },
  'project-construction-reservation': {
    badge: 'Laydown',
    title: 'Construction reservation — a temporary laydown/construction envelope.',
    tone: 'reserved',
    isInstalled: false,
  },
  'user-defined': {
    badge: 'Custom',
    title: 'User-defined geometry — dimensions entered or drawn in the studio.',
    tone: 'user',
    isInstalled: false,
  },
};

/**
 * Resolve the dimension-provenance descriptor for a generated component's
 * `dimensionBasis`. Unknown bases fall back to a neutral "user" tone rather
 * than throwing, so a future basis value degrades gracefully.
 */
export function dimensionProvenance(basis: GenerationTestFitDimensionBasis): DimensionProvenance {
  return (
    DIMENSION_PROVENANCE[basis]
    ?? { badge: String(basis), title: String(basis), tone: 'user', isInstalled: false }
  );
}

/**
 * The human-facing facts the Inspector shows for a selected site-reference
 * layer — resolved through the SAME platform label/colour tables the Site Map
 * uses (`CATEGORY_LABELS`, `SOURCE_BADGES`, `colorForCategory`), so the studio
 * never forks its own taxonomy.
 */
export interface SiteLayerDescriptor {
  name: string;
  categoryLabel: string;
  sourceLabel: string;
  geometryType: SiteLayer['geometry']['type'];
  color: string;
  visible: boolean;
  vertexCount: number;
  description?: string;
}

export function describeSiteLayer(layer: SiteLayer): SiteLayerDescriptor {
  return {
    name: layer.name,
    categoryLabel: CATEGORY_LABELS[layer.category] ?? layer.category,
    sourceLabel: SOURCE_BADGES[layer.source] ?? layer.source.replaceAll('-', ' '),
    geometryType: layer.geometry.type,
    color: colorForCategory(layer.category, layer.color),
    visible: layer.visible,
    vertexCount: layer.geometry.coordinates.length,
    ...(layer.description ? { description: layer.description } : {}),
  };
}

/* ── Category grouping (enum + user-defined) ──────────────────────────── */

/**
 * The key a layer groups under: its user-defined `customCategory` when set,
 * otherwise the enum `category`. This is the single source of truth for
 * grouping AND for the per-category hide toggle (a hidden-category key matches
 * whatever this returns).
 */
export function layerCategoryKey(layer: SiteLayer): string {
  const custom = layer.customCategory?.trim();
  return custom && custom.length > 0 ? custom : layer.category;
}

/** Human label for a category key (enum → its label; custom → itself). */
export function categoryKeyLabel(key: string): string {
  return (CATEGORY_LABELS as Record<string, string>)[key] ?? key;
}

/** True when the key is a user-defined category (not one of the enum values). */
export function isCustomCategoryKey(key: string): boolean {
  return !(key in CATEGORY_LABELS);
}

export interface StudioCategoryGroup<T> {
  key: string;
  label: string;
  custom: boolean;
  layers: T[];
}

/**
 * Group layers by {@link layerCategoryKey}, ordered by the canonical
 * `CATEGORY_ORDER` for enum categories, with any user-defined categories
 * appended in first-seen order after them. Empty groups are omitted.
 */
export function groupLayersByCategory<T extends SiteLayer>(layers: readonly T[]): StudioCategoryGroup<T>[] {
  const buckets = new Map<string, T[]>();
  for (const layer of layers) {
    const key = layerCategoryKey(layer);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(layer);
    else buckets.set(key, [layer]);
  }
  const enumOrder = new Map<string, number>(CATEGORY_ORDER.map((c, i) => [c, i]));
  return [...buckets.entries()]
    .sort(([a], [b]) => {
      const ai = enumOrder.get(a);
      const bi = enumOrder.get(b);
      if (ai != null && bi != null) return ai - bi; // both enum → canonical order
      if (ai != null) return -1;                    // enum before custom
      if (bi != null) return 1;
      return 0;                                     // both custom → first-seen (Map order)
    })
    .map(([key, groupLayers]) => ({
      key,
      label: categoryKeyLabel(key),
      custom: isCustomCategoryKey(key),
      layers: groupLayers,
    }));
}

/* ── Acoustic barriers ────────────────────────────────────────────────── */

const BARRIER_KIND_LABELS: Record<GenerationAcousticBarrier['kind'], string> = {
  wall: 'Noise wall',
  berm: 'Earth berm',
  building: 'Screening building',
};

export interface BarrierDescriptor {
  label: string;
  kindLabel: string;
  heightM: number;
  /** Barriers attenuate the noise field — the two layers are coupled. */
  affectsNoise: true;
}

export function describeBarrier(barrier: GenerationAcousticBarrier): BarrierDescriptor {
  return {
    label: barrier.label,
    kindLabel: BARRIER_KIND_LABELS[barrier.kind] ?? barrier.kind,
    heightM: barrier.heightM,
    affectsNoise: true,
  };
}

/* ── Exclusion (no-build) zones ───────────────────────────────────────── */

const EXCLUSION_KIND_LABELS: Record<GenerationLayoutExclusionZone['kind'], string> = {
  'flood-1pct': '1% annual flood zone',
  'flood-0.2pct': '0.2% annual flood zone',
  floodway: 'Floodway',
  wetland: 'Wetland',
  'steep-slope': 'Steep slope',
  custom: 'Custom exclusion',
};

const EXCLUSION_SOURCE_LABELS: Record<GenerationLayoutExclusionZone['source'], string> = {
  'fema-nfhl': 'FEMA NFHL',
  'usfws-nwi': 'USFWS NWI',
  'derived-slope': 'Derived slope',
  'user-drawn': 'User drawn',
  'site-layer': 'Site layer',
};

/** Human label for an exclusion zone's kind (shared by the tree + Inspector). */
export function exclusionKindLabel(zone: GenerationLayoutExclusionZone): string {
  if (zone.kind === 'custom') return zone.label || EXCLUSION_KIND_LABELS.custom;
  return EXCLUSION_KIND_LABELS[zone.kind] ?? zone.label;
}

export interface ExclusionDescriptor {
  label: string;
  kindLabel: string;
  sourceLabel: string;
  ringCount: number;
}

export function describeExclusion(zone: GenerationLayoutExclusionZone): ExclusionDescriptor {
  return {
    label: zone.label || exclusionKindLabel(zone),
    kindLabel: exclusionKindLabel(zone),
    sourceLabel: EXCLUSION_SOURCE_LABELS[zone.source] ?? zone.source.replaceAll('-', ' '),
    ringCount: zone.polygons.length,
  };
}
